import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, Trash2, Settings2, ShieldOff, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AppAccessDialog } from '@/components/admin/AppAccessDialog';
import { useAdminAppAccess, UserAppAccess } from '@/hooks/useAdminAppAccess';
import { toggleAdminStatus, removeAllowedEmail, updateDisplayName, AllowedEmail } from '@/services/allowedEmails';
import { appIcons, AppIcon } from '@/config/appIcons';
import { employeeNamesService } from '@/services/employeeNames';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AuthorizedEmailsProps {
  emailList: AllowedEmail[];
  onUpdate: () => Promise<void>;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export const AuthorizedEmails: React.FC<AuthorizedEmailsProps> = ({
  emailList,
  onUpdate,
  searchQuery,
  setSearchQuery
}) => {
  // Filter out invalid email entries - memoize to prevent infinite loops
  const validEmailList = useMemo(() =>
    emailList.filter(user => user && user.email),
    [emailList]
  );
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmailsForDialog, setSelectedEmailsForDialog] = useState<string[]>([]);
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [newDisplayName, setNewDisplayName] = useState('');
  
  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'toggle_admin' | 'remove_user';
    email: string;
    isManuallyAdded?: boolean;
    currentAdminStatus?: boolean;
  } | null>(null);

  const { userAppAccess, fetchUserAppAccess } = useAdminAppAccess();
  const handleSelectAllChange = useCallback((isSelected: boolean | "indeterminate") => {
    if (typeof isSelected !== "boolean") return;
    setSelectedEmails(new Set(isSelected ? validEmailList.map(user => user.email) : []));
  }, [validEmailList]);

  useEffect(() => {
    fetchUserAppAccess();
  }, [fetchUserAppAccess]);

  const handleSelectEmail = (email: string, isSelected: boolean | "indeterminate") => {
    if (typeof isSelected !== "boolean") return;
    const newSelected = new Set(selectedEmails);
    if (isSelected) {
      newSelected.add(email);
    } else {
      newSelected.delete(email);
    }
    setSelectedEmails(newSelected);
  };

  const handleOpenDialog = (emails: string[]) => {
    setSelectedEmailsForDialog(emails);
    setDialogOpen(true);
  };

  const handleEditDisplayName = async (email: string, currentDisplayName: string = '') => {
    setEditingEmail(email);
    setNewDisplayName(currentDisplayName);
  };

  const handleSaveDisplayName = async (email: string) => {
    try {
      await updateDisplayName(email, newDisplayName.trim());
      // Clear the employee names cache to force a fresh fetch
      employeeNamesService.clearCache();
      await onUpdate();
      setStatus({ type: 'success', message: 'Display name updated successfully' });
      setEditingEmail(null);
      setNewDisplayName('');
    } catch (error) {
      console.error('Error updating display name:', error);
      setStatus({ type: 'error', message: 'Failed to update display name' });
    }
  };

  const handleDialogOpenChange = async (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setSelectedEmailsForDialog([]);
      // Refresh app access data when dialog closes
      await fetchUserAppAccess();
    }
  };

  // Action Handlers
  const handleInitiateAction = (action: {
    type: 'toggle_admin' | 'remove_user';
    email: string;
    isManuallyAdded?: boolean;
    currentAdminStatus?: boolean;
  }) => {
    if (action.type === 'remove_user' && !action.isManuallyAdded) {
      setStatus({ type: 'error', message: 'Cannot remove Google Sheets email' });
      return;
    }
    setPendingAction(action);
    setConfirmOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;

    try {
      if (pendingAction.type === 'remove_user') {
        await removeAllowedEmail(pendingAction.email);
        setStatus({ type: 'success', message: 'User removed successfully' });
      } else if (pendingAction.type === 'toggle_admin') {
        await toggleAdminStatus(pendingAction.email);
        setStatus({ type: 'success', message: 'Admin status updated successfully' });
      }
      await onUpdate();
    } catch (error) {
      console.error(`Error performing ${pendingAction.type}:`, error);
      setStatus({ type: 'error', message: `Failed to ${pendingAction.type === 'toggle_admin' ? 'update admin status' : 'remove user'}` });
    } finally {
      setConfirmOpen(false);
      setPendingAction(null);
    }
  };

  const filteredUsers = validEmailList.filter(user => 
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.displayName && user.displayName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardContent className="px-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            {/* Header removed as requested, but keeping search/actions */}
          </div>
          <div className="flex items-center gap-2 w-full justify-end">
            {selectedEmails.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenDialog(Array.from(selectedEmails))}
              >
                <Settings2 className="h-4 w-4 mr-2" />
                Edit Access ({selectedEmails.size})
              </Button>
            )}
          </div>
        </div>

        {status && (
          <Alert
            variant={status.type === 'success' ? 'default' : 'destructive'}
            className="mb-4"
          >
            {status.message}
          </Alert>
        )}

        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedEmails.size > 0 && selectedEmails.size === validEmailList.length}
                    onCheckedChange={handleSelectAllChange}
                  />
                </TableHead>
                <TableHead>User</TableHead>
                <TableHead>Access</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => {
                const originalEmail = user.email ? user.email.split(' ')[0] : '';
                return (
                  <TableRow key={user.email}>
                    <TableCell>
                      <Checkbox
                        checked={selectedEmails.has(user.email)}
                        onCheckedChange={(checked) => handleSelectEmail(user.email, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{originalEmail}</span>
                          {user.isAdmin && <Badge variant="secondary" className="text-xs">Admin</Badge>}
                          {user.isManuallyAdded && <Badge variant="outline" className="text-xs border-orange-200 text-orange-600">Manual</Badge>}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {editingEmail === originalEmail ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={newDisplayName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewDisplayName(e.target.value)}
                                placeholder="Display name"
                                className="h-7 text-xs w-40"
                              />
                              <Button
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => handleSaveDisplayName(originalEmail)}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2"
                                onClick={() => setEditingEmail(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center text-sm text-muted-foreground">
                              {user.displayName || 'No display name'}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 ml-1"
                                onClick={() => handleEditDisplayName(originalEmail, user.displayName)}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {userAppAccess
                          .find((access: UserAppAccess) =>
                            user.email && access.email &&
                            access.email.toLowerCase() === user.email.toLowerCase()
                          )
                          ?.allowedApps?.map((appId: string) => {
                            const app = appIcons.find((icon: AppIcon) => icon.id === appId);
                            if (!app) return null;

                            const Icon = app.icon;
                            // Special handling for Employee Directory with custom SVG icon
                            if (app.id === 'employee-directory') {
                              return (
                                <TooltipProvider key={app.id}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className={`p-1.5 ${app.color} rounded-md shadow-sm`}>
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="14"
                                          height="14"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="white"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="h-3.5 w-3.5"
                                        >
                                          <circle cx="10" cy="7" r="4"></circle>
                                          <path d="M10.3 15H7a4 4 0 0 0-4 4v2"></path>
                                          <circle cx="17" cy="17" r="3"></circle>
                                          <path d="m21 21-1.9-1.9"></path>
                                        </svg>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>{app.name}</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            }

                            return (
                              <TooltipProvider key={app.id}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className={`p-1.5 ${app.color} rounded-md shadow-sm`}>
                                      <Icon className="h-3.5 w-3.5 text-white" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>{app.name}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDialog([user.email])}
                              >
                                <Settings2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Manage App Access</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleInitiateAction({
                                  type: 'toggle_admin',
                                  email: originalEmail,
                                  currentAdminStatus: user.isAdmin
                                })}
                              >
                                {user.isAdmin ? (
                                  <ShieldOff className="h-4 w-4 text-blue-600" />
                                ) : (
                                  <Shield className="h-4 w-4 text-muted-foreground" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {user.isAdmin ? 'Remove admin privileges' : 'Make admin'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleInitiateAction({
                                  type: 'remove_user',
                                  email: originalEmail,
                                  isManuallyAdded: user.isManuallyAdded
                                })}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove User</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <AppAccessDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        selectedEmails={selectedEmailsForDialog}
      />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.type === 'remove_user' 
                ? 'Delete User?' 
                : pendingAction?.currentAdminStatus 
                  ? 'Remove Admin Privileges?' 
                  : 'Grant Admin Privileges?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.type === 'remove_user' 
                ? `Are you sure you want to delete ${pendingAction?.email}? This action cannot be undone.`
                : pendingAction?.currentAdminStatus
                  ? `Are you sure you want to remove admin privileges from ${pendingAction?.email}? They will lose access to all admin features.`
                  : `Are you sure you want to make ${pendingAction?.email} an admin? They will have full access to all admin features.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmAction}
              className={pendingAction?.type === 'remove_user' || pendingAction?.currentAdminStatus 
                ? "bg-red-600 hover:bg-red-700" 
                : ""}
            >
              {pendingAction?.type === 'remove_user'
                ? 'Delete'
                : pendingAction?.currentAdminStatus
                  ? 'Remove Admin'
                  : 'Make Admin'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
